/**
 * Secondary IB-track spoke page content (Sec 1-4 IB): the four Year 1-4 IB
 * literals plus their FAQ + decision-support data and the builder that attaches
 * them. These are literals (not generated) because IB schemes differ from the
 * mainstream G1/G2/G3/IP streams.
 */
import { levelPageIds } from './pageIds';
import type { DecisionProofPoint, FaqItem, LevelPageContent } from './types';

function buildSecondaryStreamIbContent(
  secLevel: number,
  brandName = 'DeepThink'
): LevelPageContent {
  const spokes: Record<string, LevelPageContent> = {
    sec1IB: {
      id: levelPageIds.sec1IB,
      slug: '/sec-1-ib',
      seo: {
        title: `Sec 1 IB Math Tuition (Year 1) | ${brandName}`,
        description:
          'Sec 1 IB (International Baccalaureate) Math tuition in Singapore — Year 1 IB-track Mathematics. Weekly live classes covering the full Year 1 scheme, building toward the IB Diploma in Year 5–6.',
      },
      hero: {
        eyebrow: 'SEC 1 IB MATHEMATICS',
        headline:
          'Build a strong Year 1 foundation for the IB Diploma pathway.',
        subhead:
          'Year 1 of the IB-track Integrated Programme compresses the lower secondary syllabus, layers on enrichment, and orients students toward the IB Diploma in Year 5–6. Our weekly live classes follow the Year 1 IB-track scheme so students stay aligned to school assessments without falling behind.',
        ctaLabel: 'Book a free trial class',
      },
      syllabus: {
        title: 'What we cover (Year 1 IB-track Mathematics)',
        eyebrow: 'Syllabus',
        body: 'Year 1 of the IB-track Integrated Programme covers four terms and 14 chapters — from primes, real numbers, and basic algebra in Term 1, through equations, geometry, and ratio in Term 2, to percentages, sequences, linear functions, and area in Term 3, and finally volume, surface area, and data handling in Term 4.',
        topics: [
          {
            category: 'Term 1',
            items: [
              'Factors and Multiples: primes, HCF and LCM; squares, square roots, cubes and cube roots',
              'Real Numbers: integers; rational and irrational numbers',
              'Approximation and Estimation: significant figures and estimation',
              'Basic Algebra and Algebraic Manipulation: expansion, laws of indices, factorisation (common factors and grouping), and substitution',
            ],
          },
          {
            category: 'Term 2',
            items: [
              'Simple Equations in One Variable: linear and simple algebraic equations',
              'Angles and Parallel Lines',
              'Triangles, Quadrilaterals and Other Polygons; geometrical construction of triangles and quadrilaterals',
              'Ratio, Rate and Speed; direct and inverse proportion; scales and maps (linear and area scales)',
            ],
          },
          {
            category: 'Term 3',
            items: [
              'Percentages: discount, hire purchase, taxes, GST and commission',
              'Sequences and Number Patterns',
              'Linear Functions and Graphs: Cartesian coordinates, gradient, and the equation of a straight line',
              'Perimeters and Areas of Plane Figures: squares, rectangles, triangles, circles, parallelograms and trapeziums',
            ],
          },
          {
            category: 'Term 4',
            items: [
              'Volume and Surface Area of Prisms and Cylinders; composite solids',
              'Data Handling: frequency tables; reading and interpreting tables and statistical diagrams',
            ],
          },
        ],
        note: 'The Year 1 IB-track Mathematics syllabus is broadly consistent across local IB schools.',
      },
      challenges: {
        title: 'Common challenges for Year 1 IB students',
        body: 'Year 1 in an IB-track school is a real step up. Students often share these pressure points:',
        items: [
          {
            title: 'Algebra arrives faster and goes deeper',
            description:
              'Year 1 covers basic algebra, expansion, laws of indices, and two factorisation techniques (common factors and grouping) — all in Term 1. Students who were strong in arithmetic but new to working with letters often stall here.',
          },
          {
            title:
              'School assessments measure understanding, not just procedure',
            description:
              'IB-track schools assess why a method works, not only whether students can apply it. Year 1 papers reward clear reasoning, not memorised steps.',
          },
          {
            title: 'Pace means small gaps compound quickly',
            description:
              "A shaky understanding of, say, indices in Term 1 makes algebraic manipulation harder for the rest of the year. IB-track Year 1 doesn't leave room to revisit later — gaps need to be fixed when they appear.",
          },
          {
            title: 'A new school environment on top of new content',
            description:
              'Year 1 is a transition year — new school, new subjects, new expectations. A calm, consistent space outside school helps students focus on understanding the Math without the noise.',
          },
        ],
      },
      howWeHelp: {
        title: 'How DeepThink helps Sec 1 IB students',
        body: 'Our Sec 1 IB class is built around the reality of Year 1 IB-track Mathematics:',
        points: [
          {
            title: 'Built around the Year 1 IB-track scheme',
            description:
              'The Year 1 IB-track Mathematics syllabus is broadly consistent across local IB schools. Our class teaches that scope directly.',
          },
          {
            title: 'Build algebra so it actually makes sense',
            description:
              'We teach why expansion, indices, and factorisation rules work, then drill them through targeted practice so they become reliable tools by the time students hit equations and inequalities.',
          },
          {
            title: 'Catch gaps before they compound',
            description:
              'Our practice platform marks instantly and surfaces topic-level weaknesses every week, so a Term 1 indices gap is fixed before it shows up in Term 2 algebra.',
          },
          {
            title: 'Reasoning, not just answers',
            description:
              'IB-track schools assess working and reasoning. We teach students to explain their steps clearly, not just write the final number.',
          },
        ],
        closingLine:
          'A strong Year 1 sets the tone for the rest of the IB-track journey through to the Diploma in Year 5–6.',
      },
      ctaSection: {
        title: 'Start with a trial class',
        body: 'See how DeepThink helps Sec 1 IB students build a confident Year 1 foundation toward the IB Diploma.',
        ctaLabel: 'Book your free trial',
      },
    },

    sec2IB: {
      id: levelPageIds.sec2IB,
      slug: '/sec-2-ib',
      seo: {
        title: `Sec 2 IB Math Tuition (Year 2) | ${brandName}`,
        description:
          'Sec 2 IB (International Baccalaureate) Math tuition in Singapore — Year 2 IB-track Mathematics. Weekly live classes covering the full Year 2 scheme, building toward the IB Diploma in Year 5–6.',
      },
      hero: {
        eyebrow: 'SEC 2 IB MATHEMATICS',
        headline:
          'Bridge Year 1 foundations into algebraic depth and geometry.',
        subhead:
          'Year 2 of the IB-track Integrated Programme extends Year 1 algebra into quadratic equations and functions, introduces Pythagoras and trigonometry, and lays the groundwork for the Advanced Mathematics course in Year 3. Our weekly live classes follow the Year 2 IB-track scheme so students stay aligned to school assessments without falling behind.',
        ctaLabel: 'Book a free trial class',
      },
      syllabus: {
        title: 'What we cover (Year 2 IB-track Mathematics)',
        eyebrow: 'Syllabus',
        body: 'Year 2 of the IB-track Integrated Programme extends Year 1 across four terms and 11 chapters. Term 1 covers proportion, central tendency, linear inequalities, simultaneous equations, and algebraic expansion and factorisation. Term 2 moves into algebraic fractions, quadratic equations and functions, indices, and standard form. Term 3 introduces Pythagoras, trigonometric ratios, and the volume and surface area of pyramids, cones, and spheres. Term 4 closes with symmetry.',
        topics: [
          {
            category: 'Term 1',
            items: [
              'Proportion and Measure of Central Tendency: direct and inverse proportion; mean, median, mode',
              'Linear Inequalities: solving simple inequalities and word problems',
              'Linear Equations in Two Variables: simultaneous equations by elimination, substitution, and graphical methods',
              'Expansion and Factorisation of Algebraic Expressions: common factors, grouping, special algebraic rules, quadratic factorisation',
            ],
          },
          {
            category: 'Term 2',
            items: [
              'Algebraic Fractions and Formulae: simplification, multiplication, division, addition, subtraction; equations involving algebraic fractions; changing subject of formula',
              'Quadratic Equations: rooting, factorisation, completing the square, quadratic formula; applications',
              'Quadratic Functions and Graphs: drawing and interpreting quadratic graphs; applications',
              'Indices and Standard Form: laws of indices; equations involving indices; standard form and operations',
            ],
          },
          {
            category: 'Term 3',
            items: [
              "Pythagoras' Theorem: applications including pyramids, cones, and spheres; converse for identifying right-angled triangles",
              'Trigonometric Ratios: sine, cosine, and tangent of acute angles; applications',
              'Volume and Surface Area of Pyramids, Cones, and Spheres',
            ],
          },
          {
            category: 'Term 4',
            items: ['Symmetry: line symmetry; rotational symmetry and order'],
          },
        ],
        note: 'The Year 2 IB-track Mathematics syllabus is broadly consistent across local IB schools.',
      },
      challenges: {
        title: 'Common challenges for Year 2 IB students',
        body: 'Year 2 builds heavily on Year 1 foundations and accelerates into algebraic depth. Students often share these pressure points:',
        items: [
          {
            title:
              'Quadratic equations introduce four solution methods at once',
            description:
              'Year 2 covers rooting, factorisation, completing the square, and the quadratic formula — students need fluency in choosing the right method for each problem, not just memorising the steps for one.',
          },
          {
            title: 'Algebraic fractions get harder fast',
            description:
              'Year 2 extends fractions to multiplication, division, addition, subtraction, and rearranging formulae. Small slips in Year 1 algebra can cascade into stalled work here.',
          },
          {
            title: 'Pythagoras and trigonometry layer onto geometry',
            description:
              'Trigonometry and Pythagoras require precise diagram reading and sustained working. Students who skim diagrams or skip steps lose marks they would otherwise earn.',
          },
          {
            title: 'Year 3 raises the stakes — gaps need closing now',
            description:
              'In Year 3, IB-track students take both Core Mathematics and a separate Advanced Mathematics course. Year 2 weaknesses make that split harder to manage.',
          },
        ],
      },
      howWeHelp: {
        title: 'How DeepThink helps Sec 2 IB students',
        body: 'Our Sec 2 IB class is built around Year 2 IB-track Mathematics:',
        points: [
          {
            title: 'Built around the Year 2 IB-track scheme',
            description:
              'The Year 2 IB-track Mathematics syllabus is broadly consistent across local IB schools. Our class teaches that scope directly.',
          },
          {
            title: 'Build quadratic and algebraic fluency the right way',
            description:
              'We teach the four quadratic methods systematically and drill students on choosing the right one. Algebraic fractions and formula manipulation are practised until they become reliable.',
          },
          {
            title: 'Make trigonometry visual and intuitive',
            description:
              'We anchor trigonometry in the geometry it comes from, so students reason through diagrams instead of pattern-matching to memorised setups.',
          },
          {
            title: 'Close gaps before the Year 3 split',
            description:
              'Year 3 layers Advanced Mathematics on top of Core. We make sure Year 2 algebra and equations are bulletproof so the Year 3 jump is manageable.',
          },
        ],
        closingLine:
          'A solid Year 2 makes the Year 3 Core + Advanced split a stretch, not a shock.',
      },
      ctaSection: {
        title: 'Start with a trial class',
        body: 'See how DeepThink helps Sec 2 IB students consolidate Year 2 foundations toward the IB Diploma.',
        ctaLabel: 'Book your free trial',
      },
    },

    sec3IB: {
      id: levelPageIds.sec3IB,
      slug: '/sec-3-ib',
      seo: {
        title: `Sec 3 IB Math Tuition (Year 3 Core + Advanced) | ${brandName}`,
        description:
          'Sec 3 IB (International Baccalaureate) Math tuition in Singapore — Year 3 IB-track Core and Advanced Mathematics taught in parallel, building toward the IB Diploma in Year 5–6.',
      },
      hero: {
        eyebrow: 'SEC 3 IB MATHEMATICS',
        headline: 'Take Year 3 Core and Advanced Mathematics in stride.',
        subhead:
          'Year 3 of the IB-track Integrated Programme splits mathematics into two parallel courses: Core Mathematics, which extends the Year 2 syllabus into upper secondary depth; and Advanced Mathematics, which introduces A-Math content — algebraic fractions, polynomials, functions, exponentials, logarithms, and trigonometry — that lands students within reach of the IB Diploma.',
        ctaLabel: 'Book a free trial class',
      },
      syllabus: {
        title: 'What we cover (Year 3 Core + Advanced Mathematics)',
        eyebrow: 'Syllabus',
        body: 'Year 3 IB-track students take two mathematics courses in parallel. Core Mathematics covers simultaneous equations, quadratics, indices, surds, logarithms, trigonometry (sine and cosine rule, area, bearings, 3D problems), linear inequalities, theory of quadratic equations, coordinate geometry, graphs of functions, and mensuration of pyramids, cones, and spheres. Advanced Mathematics covers algebraic fractions, polynomials, partial fractions, functions (composite, inverse, modulus, transformations), exponentials, logarithms, basic and intermediate trigonometry. Our class teaches the Year 3 Core and Advanced curriculum used at local IB schools.',
        topics: [
          {
            category: 'Year 3 Core Mathematics',
            items: [
              'Simultaneous Equations: linear–linear and linear–non-linear in two unknowns',
              'Solutions to Quadratic Equations: completing the square, quadratic formula, graphs and curve sketching',
              'Indices, Surds, and Logarithms: laws of indices, surds, exponential equations',
              'Solutions of a Triangle: trigonometric ratios for acute and obtuse angles, sine and cosine rule, area, bearings, 3D problems',
              'Linear Inequalities: properties, solving in one variable, word problems',
              'Theory of Quadratic Equations, Functions, and Inequalities: sum and product of roots, quadratic inequalities, discriminant and nature of roots',
              'Coordinate Geometry: midpoint, distance, gradient, equation of a straight line, collinear points, parallel and perpendicular lines, perpendicular bisectors, area of rectilinear figures',
              'Graphs of Functions: cubic, reciprocal, exponential; graphical solutions',
              'Mensuration: pyramids, cones, spheres',
            ],
          },
          {
            category: 'Year 3 Advanced Mathematics',
            items: [
              'Algebraic Fractions: simplifying and solving',
              'Polynomials: identities, long division, remainder and factor theorems, cubic equations',
              'Partial Fractions: linear, repeated linear, and irreducible quadratic factors',
              'Functions: relations and functions, composite, inverse, absolute-valued functions, transformations of graphs',
              'Exponentials and Logarithms: properties and laws of logarithms, logarithmic and exponential equations, graphs and applications',
              'Basic Trigonometry: basic angles, simple trigonometric equations, graphs of sine, cosine, and tangent',
              'Intermediate Trigonometry: proving identities, solving trigonometric equations',
            ],
          },
        ],
        note: 'The Year 3 Core and Advanced Mathematics syllabus is consistent across local IB-track schools.',
      },
      challenges: {
        title: 'Common challenges for Year 3 IB students',
        body: 'Year 3 introduces a step change — two mathematics courses in parallel — and the depth jumps sharply. Students often share these pressure points:',
        items: [
          {
            title: 'Two courses, two assessment streams',
            description:
              'Core and Advanced are graded separately, with distinct papers, distinct topics, and distinct expectations. Students who try to lump them together often underperform on both.',
          },
          {
            title: 'Advanced Mathematics introduces A-Math depth quickly',
            description:
              'Polynomials, partial fractions, function transformations, and trigonometric identities arrive in rapid sequence. Students need fluent algebra and a clear mental model of functions to keep up.',
          },
          {
            title: 'Coordinate geometry and trigonometry get layered',
            description:
              'Sine rule, cosine rule, 3D problems, perpendicular bisectors, and gradients all show up in mixed-topic problems. Students need to switch context fluidly.',
          },
          {
            title: 'IB Diploma is now visible on the horizon',
            description:
              'Year 3 is when the IB pathway becomes concrete. Students who treat Year 3 as a holding year often arrive in Year 4 short of the foundation they need for AA HL or AI HL.',
          },
        ],
      },
      howWeHelp: {
        title: 'How DeepThink helps Sec 3 IB students',
        body: 'Our Sec 3 IB class is built around the reality of two mathematics courses running in parallel:',
        points: [
          {
            title: 'Teach Core and Advanced as separate disciplines',
            description:
              'Each course has its own pacing, its own assessment style, and its own conceptual demands. We treat them that way — not as one bigger maths class.',
          },
          {
            title: 'Built around the Year 3 IB-track scheme',
            description:
              'The Year 3 Core and Advanced Mathematics syllabus is broadly consistent across local IB schools. Our class teaches that scope directly.',
          },
          {
            title: 'Build the function and algebra fluency Advanced demands',
            description:
              'Composite functions, inverse functions, polynomials, and trigonometric identities are all built on flexible algebra. We make sure that fluency is there before diving into the harder topics.',
          },
          {
            title: 'Anchor reasoning, not just procedure',
            description:
              'IB-track schools assess understanding. We teach students to explain identities and proofs, not just compute.',
          },
        ],
        closingLine:
          'Year 3 is the leap into IB-level mathematics. We make sure students land it cleanly.',
      },
      ctaSection: {
        title: 'Start with a trial class',
        body: 'See how DeepThink helps Sec 3 IB students handle both Core and Advanced Mathematics with confidence.',
        ctaLabel: 'Book your free trial',
      },
    },

    sec4IB: {
      id: levelPageIds.sec4IB,
      slug: '/sec-4-ib',
      seo: {
        title: `Sec 4 IB Math Tuition (Year 4 Core + Advanced, Pre-IB) | ${brandName}`,
        description:
          'Sec 4 IB (International Baccalaureate) Math tuition in Singapore — Year 4 IB-track Core and Advanced Mathematics (calculus, integration, kinematics) bridging directly into the IB Diploma in Year 5–6.',
      },
      hero: {
        eyebrow: 'SEC 4 IB MATHEMATICS',
        headline: 'Bridge Year 4 directly into the IB Diploma pathway.',
        subhead:
          'Year 4 of the IB-track Integrated Programme is the bridge into the IB Diploma. Core Math consolidates the upper secondary content — set theory, congruency and similarity, circle theorems, vectors, statistics — while Advanced Math introduces calculus: differentiation, rates of change, maxima and minima, integration, and kinematics. Our class follows the Year 4 IB-track curriculum so students arrive at Year 5 ready for AA or AI at HL or SL.',
        ctaLabel: 'Book a free trial class',
      },
      syllabus: {
        title: 'What we cover (Year 4 Core + Advanced Mathematics)',
        eyebrow: 'Syllabus',
        body: 'Year 4 IB-track students continue with two parallel mathematics courses. Core Math covers algebraic manipulation, set theory, congruency and similarity, circle theorems, arc length and sector area, vectors in 2D, linear law, three statistics units, mathematics in practical situations, graphs in practical situations, number patterns, and probability. Advanced Math is the calculus year — binomial theorem, further trigonometric identities, differentiation, rates of change, maxima and minima, derivatives of trigonometric, exponential and logarithmic functions, integration, and applications of integration including area between curves and kinematics.',
        topics: [
          {
            category: 'Year 4 Core Mathematics',
            items: [
              'Algebraic Manipulation',
              'Set Theory: notation, language, Venn diagrams',
              'Congruency and Similarity: congruent and similar triangles, area and volume of similar figures',
              'Circle Theorem: symmetric and angle properties of circles',
              'Arc Length, Area of Sector, and Radian Measure',
              'Vectors in Two Dimensions: vectors on the coordinate plane, applications in geometry',
              'Linear Law',
              'Statistics (I): frequency distribution, histograms, frequency polygons; mean, median, mode',
              'Statistics (II): cumulative frequency curves; box-and-whisker diagrams',
              'Statistics (III): standard deviation',
              'Mathematics in Practical Situations: profit and loss, simple and compound interest, hire-purchase, utility bills, money exchange, taxation',
              'Graphs in Practical Situations: tables, charts, and graphs; distance–time and speed–time graphs',
              'Number Patterns',
              'Probability',
            ],
          },
          {
            category: 'Year 4 Advanced Mathematics',
            items: [
              'Binomial Theorem: introduction and binomial expansion',
              'Further Trigonometric Identities: addition formulae and double-angle formulae',
              'Differentiation: first principles, basic differentiation, chain rule, product rule, quotient rule, equations of tangent and normal',
              'Rates of Change: increasing and decreasing functions; rates of change',
              'Maxima and Minima: stationary points; maxima and minima problems',
              'Derivatives of Simple Trigonometric Functions',
              'Derivatives of Exponential and Logarithmic Functions',
              'Integration: basic integration, linear factor, trigonometric functions, exponential functions, 1/x, definite integrals',
              'Application of Integration — Area of a Region: area between a curve and an axis',
              'Application of Integration — Kinematics: displacement, velocity, acceleration',
            ],
          },
        ],
        note: 'The Year 4 Core and Advanced Mathematics syllabus is consistent across local IB-track schools.',
      },
      challenges: {
        title: 'Common challenges for Year 4 IB students',
        body: 'Year 4 brings calculus into the picture and is the last year before the IB Diploma. Students often share these pressure points:',
        items: [
          {
            title: 'Calculus arrives all at once',
            description:
              'Differentiation rules, rates of change, maxima and minima, integration, area between curves, and kinematics — Year 4 Advanced packs them in. Students who treat them as a series of formulae rather than a coherent system tend to plateau.',
          },
          {
            title: 'Core breadth competes with Advanced depth',
            description:
              'Statistics (three units), set theory, circle theorems, vectors, probability — Core Math is wide. Students juggling that with calculus often under-prepare for one or both.',
          },
          {
            title: 'IB papers reward setup, not shortcuts',
            description:
              'IB-style questions reward clear definitions, justified working, and full solutions. Students who jump to answers lose marks even when the answer is right.',
          },
          {
            title: 'Year 5 IB transition needs a clean handoff',
            description:
              'AA and AI start in Year 5, building straight on Year 4 calculus and statistics. Gaps now mean catch-up later, against IB Diploma deadlines.',
          },
        ],
      },
      howWeHelp: {
        title: 'How DeepThink helps Sec 4 IB students',
        body: 'Our Sec 4 IB class is built around the calculus year and the IB Diploma transition:',
        points: [
          {
            title: 'Teach calculus as a connected system',
            description:
              'Differentiation, integration, and their applications fit together. We make those connections explicit so students reason from principles, not from a list of formulae.',
          },
          {
            title: 'Built around the Year 4 IB-track scheme',
            description:
              'The Year 4 Core and Advanced Mathematics syllabus is broadly consistent across local IB schools. Our class teaches that scope directly.',
          },
          {
            title: 'Match Core breadth without dropping depth',
            description:
              'Statistics, set theory, vectors, and probability all need targeted practice. We sequence Core revision so nothing is left under-prepared at exam time.',
          },
          {
            title: 'Set up the IB Diploma transition',
            description:
              'Year 4 is the runway into AA / AI. We focus on the calculus, statistics, and reasoning skills that Year 5 IB classes assume from day one.',
          },
        ],
        closingLine:
          'Year 4 is the launching pad for the IB Diploma. We make sure students take off cleanly.',
      },
      ctaSection: {
        title: 'Start with a trial class',
        body: 'See how DeepThink helps Sec 4 IB students consolidate Year 4 Core and Advanced Mathematics ahead of the IB Diploma.',
        ctaLabel: 'Book your free trial',
      },
    },
  };
  return spokes[`sec${secLevel}IB`];
}

const IB_FAQS: Record<string, FaqItem[]> = {
  '1-ib': [
    {
      question: 'What does Sec 1 IB Math (Year 1) cover at ACS(I) and SJI?',
      answer:
        'Year 1 IB-track Mathematics runs across four terms and 14 chapters. Term 1 covers primes, real numbers, approximation, and basic algebra (expansion, laws of indices, and factorisation by common factors and grouping). Term 2 introduces simple equations, angles and parallel lines, polygons, and ratio, rate, speed and proportion. Term 3 moves into percentages, sequences, linear functions and graphs, and perimeter and area of plane figures. Term 4 closes with volume and surface area of prisms and cylinders, and data handling. The scheme is broadly consistent across local IB-track schools.',
    },
    {
      question:
        'Which Singapore schools offer the IB-track Integrated Programme?',
      answer:
        "Two: Anglo-Chinese School (Independent), commonly known as ACS(I), and St. Joseph's Institution (SJI). Both are six-year IP schools whose pre-university qualification is the IB Diploma rather than the GCE A-Level. Students enter at Sec 1 through PSLE posting or DSA-Sec, follow the IB-track scheme in Year 1–4, and sit the IB Diploma in Year 5–6. ACS(I) also runs a parallel A-Level track for students who prefer it.",
    },
    {
      question:
        'How is Year 1 IB-track Math different from mainstream Sec 1 G3?',
      answer:
        'Topic overlap with G3 is high — algebra, equations, geometry, and statistics all appear in both. The differences are pace, depth, and assessment culture. Year 1 IB-track papers reward reasoning and explanation, not just procedure, and schools expect students to handle unfamiliar problem framings and proof-style steps that mainstream G3 introduces later. The destination is also different — the IB Diploma in Year 5–6, not the SEC exam in Sec 4.',
    },
    {
      question:
        'My child just entered ACS(I) or SJI — when should they start tuition?',
      answer:
        "The first term of Year 1 is the best window to assess fit. Term 1 alone introduces basic algebra, expansion, indices, and two factorisation techniques — and pace matters because Year 1 doesn't loop back to revisit earlier topics. If your child is keeping up comfortably, they may not need support. If they are spending excessive time on homework, scoring noticeably below their primary baseline, or losing confidence with the algebra, starting early prevents Term 1 gaps from showing up in Term 2 equations and ratio.",
    },
    {
      question: 'Do Sec 1 IB students sit the SEC exam or O-Levels?',
      answer:
        'No. IB-track students bypass the national secondary exam entirely. The Singapore-Cambridge SEC replaces O-Level, N(A)-Level, and N(T)-Level for mainstream G1, G2 and G3 students from 2027, but ACS(I) and SJI students do not sit it. Internal school assessments across Year 1–4 serve the same gatekeeping function — they shape Core and Advanced placement in Year 3, and the Analysis and Approaches (AA) versus Applications and Interpretation (AI) decision in Year 5.',
    },
    {
      question: 'How does Year 1 IB-track Math prepare for the IB Diploma?',
      answer:
        'Year 1 builds the algebra and reasoning that everything later depends on. Indices and factorisation underpin Year 2 quadratics and Year 3 polynomial work; linear functions and coordinate basics seed Year 3 graphs and Year 4 calculus. Most importantly, the habit of writing clear, justified working — which Year 1 IB-track papers already reward — is exactly what IB Diploma examiners look for in Year 5–6. The IB pathway is long but continuous, and Year 1 sets the tone for the whole journey.',
    },
  ],
  '2-ib': [
    {
      question: 'What does Sec 2 IB Math (Year 2) cover at ACS(I) and SJI?',
      answer:
        "Year 2 IB-track runs across four terms and 11 chapters. Term 1 covers proportion and central tendency, linear inequalities, simultaneous linear equations (elimination, substitution, graphical), and algebraic expansion and factorisation including quadratics. Term 2 moves into algebraic fractions and formulae, quadratic equations and functions, and indices and standard form. Term 3 introduces Pythagoras' theorem, trigonometric ratios of acute angles, and the volume and surface area of pyramids, cones, and spheres. Term 4 closes with line and rotational symmetry. The scheme is broadly consistent across local IB-track schools.",
    },
    {
      question:
        'How does Year 2 IB Math compare to Year 2 IP at A-Level schools (RI, HCI, NYGH, etc.)?',
      answer:
        "Sec 2 content overlaps closely — quadratics, simultaneous equations, Pythagoras, and trigonometric ratios appear in both pathways. The difference is destination. IP–A-Level schools angle Year 2 toward H2 Math; ACS(I) and SJI angle Year 2 toward the IB Diploma's Analysis and Approaches (AA) or Applications and Interpretation (AI) courses. That shows up in problem framing — IB-track Year 2 papers more often ask for written reasoning, modelling, and interpretation alongside the calculation.",
    },
    {
      question:
        'My child handled Year 1 fine but is struggling with Year 2 quadratics — is that normal?',
      answer:
        'It\'s the most common Year 2 wall. Quadratic equations, completing the square, the quadratic formula, and quadratic functions all arrive in Term 2 and assume fluent algebra from Year 1 — expansion, factorisation, and indices in particular. If those Year 1 foundations aren\'t fully secure, quadratics will expose the gap quickly. The fix is rarely "more practice on quadratics"; it is repairing the algebra that quadratics sit on.',
    },
    {
      question:
        'Is Year 2 the right time to be looking at A-Math or H2 Math content?',
      answer:
        'The more useful framing is that Year 2 prepares students for Year 3, when IB-track schools split mathematics into Core and Advanced. Solid Year 2 fluency in algebraic fractions, quadratics, indices, and trigonometry gives students the headroom they need when Advanced introduces polynomials, partial fractions, and trigonometric identities in Year 3. Looking ahead at A-Math content out of context is less useful than making Year 2 watertight.',
    },
    {
      question: 'How does Year 2 IB-track compare with mainstream Sec 2 G3?',
      answer:
        'Topic list is similar — quadratics, Pythagoras, trigonometric ratios, simultaneous equations all appear in both. Year 2 IB-track papers expect more justification per question, more comfort with unfamiliar problem types, and more multi-step reasoning. Where G3 problems tend to be procedural, IB-track problems frequently ask students to explain why a method is valid, not just apply it.',
    },
    {
      question: 'Does the SEC exam affect Sec 2 IB students?',
      answer:
        'No. ACS(I) and SJI students do not sit the SEC, nor do they sit O-Levels. The Singapore-Cambridge SEC replaces national secondary exams for mainstream students from 2027, but IB-track students continue through internal Year 1–4 assessments straight to the IB Diploma in Year 5–6. Year 2 results still matter — they inform Core versus Advanced pacing decisions in Year 3 and feed into the AA versus AI selection that follows.',
    },
  ],
  '3-ib': [
    {
      question:
        'What does Sec 3 IB Math (Year 3 Core and Advanced) cover at ACS(I) and SJI?',
      answer:
        'Year 3 IB-track students take two parallel mathematics courses. Core Mathematics covers simultaneous equations (linear–linear and linear–non-linear), quadratic theory (sum and product of roots, discriminant, quadratic inequalities), indices, surds, logarithms, trigonometry (sine and cosine rule, area, bearings, 3D problems), linear inequalities, coordinate geometry, graphs of cubic, reciprocal and exponential functions, and mensuration of pyramids, cones and spheres. Advanced Mathematics covers algebraic fractions, polynomials and the remainder and factor theorems, partial fractions, functions (composite, inverse, modulus, transformations), exponentials and logarithms, and trigonometry (basic angles, equations, and proving identities).',
    },
    {
      question: 'Why does Year 3 split into Core and Advanced Mathematics?',
      answer:
        'Year 3 is the deliberate step-up to IB-level mathematics. Core extends the Year 2 syllabus into upper-secondary depth and locks in the bread-and-butter content every IB student needs. Advanced introduces A-Math-level material — polynomials, partial fractions, function transformations, trigonometric identities — that builds toward the AA Higher Level pathway in Year 5–6. Both courses are graded separately on distinct papers, so students plan revision around two assessment streams, not one.',
    },
    {
      question: 'How is Sec 3 IB Advanced Math different from O-Level A-Math?',
      answer:
        'Topic overlap is significant — polynomials, partial fractions, exponentials and logarithms, and trigonometric identities appear in both. The differences are pace and framing. Year 3 Advanced moves faster, expects deeper mastery of function-style thinking (composite, inverse, modulus, transformations) earlier, and embeds more reasoning-style questions. Many IB Advanced classes are also assessed in formats closer to the IB Diploma than to O-Level A-Math.',
    },
    {
      question:
        'Year 3 feels like a big jump — what should students prioritise?',
      answer:
        'Algebra fluency and function thinking. Almost every Year 3 Advanced topic — polynomials, partial fractions, composite and inverse functions, modulus equations, trigonometric identities — assumes that algebraic manipulation is automatic. If a student is still slow on expansion, factorisation, or indices, every Advanced topic feels harder than it should. Repair the Year 1–2 algebra first, and Advanced becomes about ideas rather than computation.',
    },
    {
      question: 'How does Year 3 Core compare with mainstream Sec 3 G3?',
      answer:
        'Core covers similar ground — quadratics, indices, trigonometry, coordinate geometry — but expects more conceptual depth and more comfort with unfamiliar contexts. IB-track schools also assess Core differently from G3: more justification, more written reasoning, less reliance on memorised procedure. The destination is the IB Diploma, so even Core builds reasoning habits that AA and AI papers will reward in Year 5–6.',
    },
    {
      question:
        'Should Sec 3 IB students already be choosing between AA and AI for the Diploma?',
      answer:
        'It is still early to commit, but Year 3 is when the trajectory becomes visible. Students who handle Advanced comfortably — function transformations, identities, polynomial work — usually have AA HL or AA SL in their range. Students who find Advanced consistently hard are often a better fit for AI, which leans on application and modelling rather than abstract proof. The decision is finalised in Year 4–5, but the patterns that drive it are forming now.',
    },
  ],
  '4-ib': [
    {
      question:
        'What does Sec 4 IB Math (Year 4 Core and Advanced) cover at ACS(I) and SJI?',
      answer:
        'Year 4 continues with Core and Advanced Mathematics in parallel. Core Math covers algebraic manipulation, set theory and Venn diagrams, congruency and similarity, circle theorems, arc length and sector area with radian measure, vectors in 2D, linear law, three statistics units (frequency distribution, cumulative frequency, standard deviation), mathematics in practical situations (interest, hire-purchase, taxation), graphs in practical situations, number patterns, and probability. Advanced Math is the calculus year — binomial theorem, further trigonometric identities (addition and double-angle), differentiation (chain, product and quotient rules, tangent and normal), rates of change, maxima and minima, derivatives of trigonometric, exponential and logarithmic functions, integration, and applications including area between curves and kinematics.',
    },
    {
      question: 'Do Sec 4 IB students sit the O-Level Math exam?',
      answer:
        'No. ACS(I) and SJI students do not sit O-Levels or the new SEC. Year 4 ends with internal school assessments that determine readiness for the IB Diploma — including which mathematics course (Analysis and Approaches, or Applications and Interpretation) and which level (Higher Level or Standard Level) a student is placed into for Year 5. Year 4 results carry real consequence, but the assessment is internal rather than national.',
    },
    {
      question: 'How does Year 4 IB Advanced Math compare with O-Level A-Math?',
      answer:
        'The two overlap on calculus, trigonometric identities, and binomial theorem, but Year 4 Advanced reaches further. Differentiation of trigonometric, exponential and logarithmic functions, applications of integration to area between curves and kinematics, and the depth of further trigonometry sit at or beyond A-Math. Many Year 4 Advanced topics are normally H2 Math content. Year 4 Advanced is a deliberate bridge into IB Diploma mathematics, so the depth and pace exceed what an O-Level Sec 4 student typically encounters.',
    },
    {
      question:
        "My child's Year 4 IB results are slipping — what's the right response?",
      answer:
        'Slipping results in Year 4 almost always trace back to Year 3 — usually to the function and algebraic fluency that calculus assumes, or to Year 3 trigonometric identities that resurface throughout Advanced. Differentiation looks like the symptom; the cause is upstream. The fix is to identify the specific Year 3 gaps rather than re-teach calculus from scratch. Once those foundations are repaired, Year 4 calculus typically clicks back into place.',
    },
    {
      question:
        'How does Year 4 prepare students for AA or AI in the IB Diploma?',
      answer:
        'Year 4 is the runway. Differentiation, integration, kinematics, statistics, set theory, vectors, and probability all map directly onto Year 5–6 IB content — AA picks up calculus and analysis; AI picks up statistics, modelling, and applied probability. Students who consolidate Year 4 cleanly enter Year 5 ready; students with Year 4 gaps spend Year 5 doing catch-up against IB internal assessment deadlines.',
    },
    {
      question: 'When is the AA vs AI and HL vs SL decision usually finalised?',
      answer:
        'Schools typically formalise the decision late in Year 4 once internal exam results are in. The factors are: comfort with calculus and abstract reasoning (AA HL is the most demanding pure-math path), strength in modelling and data interpretation (AI plays to those strengths), and post-IB plans (some overseas STEM programmes prefer AA HL). Students who try sample AA and AI internal questions in Year 4 tend to get the cleanest read on which course fits.',
    },
  ],
};

const IB_DECISION: Record<string, DecisionProofPoint[]> = {
  '1-ib': [
    {
      title:
        'Your child has just entered ACS(I) or SJI and is finding Year 1 unexpectedly hard',
      description:
        'IB-track schools expect faster algebra, deeper reasoning, and more independent problem-solving from Term 1. Even academically strong students may need support adjusting to the IB-track culture before Term 1 algebra gaps compound through the year.',
    },
    {
      title: 'Term 1 algebra is exposing gaps from primary school',
      description:
        "Year 1 introduces basic algebra, expansion, indices, and two factorisation techniques inside Term 1 alone. Students whose primary arithmetic was procedural rather than conceptual often stall here — and the Year 1 pace doesn't loop back to revisit.",
    },
    {
      title:
        "You want continuous support so Year 1 gaps don't compound through to the IB Diploma",
      description:
        'The IB-track is a six-year continuous programme, and Year 1 foundations carry all the way to the Diploma in Year 5–6. Proactive weekly support keeps students ahead of difficulties rather than reacting to declining marks two terms later.',
    },
  ],
  '2-ib': [
    {
      title: 'Year 2 quadratics are revealing weak Year 1 algebra',
      description:
        'Quadratic equations, completing the square, and quadratic functions all assume fluent expansion, factorisation, and indices from Year 1. If those Year 1 foundations slipped, Term 2 of Year 2 is where the gap shows up sharply — usually as a dip in school marks rather than a clear topic struggle.',
    },
    {
      title:
        'Internal results in Year 2 will shape Year 3 Core and Advanced placement',
      description:
        'ACS(I) and SJI use Year 2 performance to inform pacing decisions for the Core and Advanced split that begins in Year 3. Consistent Year 2 results keep options open; a wobble now can narrow them before Year 3 has even started.',
    },
    {
      title: "Your child can compute but can't yet explain why a method works",
      description:
        "IB-track Year 2 papers reward reasoning and written justification, not just correct answers. Students who calculate well but can't articulate the underlying logic typically need targeted coaching in mathematical communication — exactly what IB Diploma examiners will look for in Year 5–6.",
    },
  ],
  '3-ib': [
    {
      title:
        'Two parallel courses (Core and Advanced) are pulling against each other',
      description:
        'Year 3 IB-track students sit two separately-graded mathematics courses with distinct topics, distinct papers, and distinct expectations. Students who try to revise them as one bigger maths class typically under-prepare for both.',
    },
    {
      title:
        'Advanced Mathematics is introducing A-Math depth faster than expected',
      description:
        'Polynomials, partial fractions, function transformations, and trigonometric identities arrive in rapid sequence in Year 3 Advanced. Without fluent algebra and a clear mental model of functions, each new topic feels harder than the last.',
    },
    {
      title: 'The AA versus AI Diploma decision is becoming visible',
      description:
        "By Year 3, a student's comfort with abstract reasoning versus application often signals whether they are heading toward Analysis and Approaches (AA) or Applications and Interpretation (AI), and at HL or SL. Strong Year 3 work keeps the most options open for that decision in Year 4–5.",
    },
  ],
  '4-ib': [
    {
      title:
        'Year 4 calculus is exposing Year 3 function and trigonometry gaps',
      description:
        'Differentiation, integration, and kinematics all assume fluent function thinking and trigonometric identities from Year 3. When Year 4 results slip, the cause is almost always upstream — and the fix is targeted Year 3 repair, not more calculus practice.',
    },
    {
      title:
        'Internal assessments now decide AA vs AI and HL vs SL placement for Year 5',
      description:
        'Year 4 internal results carry real weight at ACS(I) and SJI — they determine which IB Diploma course (Analysis and Approaches or Applications and Interpretation) and which level (Higher Level or Standard Level) a student is placed into for Year 5–6. Underperformance here narrows Diploma options.',
    },
    {
      title: 'The bridge into the IB Diploma is now imminent',
      description:
        'Year 5 AA and AI courses build directly on Year 4 calculus, statistics, and probability. Students who finish Year 4 with secure foundations enter Year 5 ready; those with gaps spend Year 5 doing catch-up against IB internal assessment deadlines.',
    },
  ],
};

export function buildSecondaryIbContent(
  secLevel: number,
  brandName = 'DeepThink'
): LevelPageContent {
  const content = buildSecondaryStreamIbContent(secLevel, brandName);
  const dataKey = `${secLevel}-ib`;
  return {
    ...content,
    ...(IB_FAQS[dataKey] && { faqItems: IB_FAQS[dataKey] }),
    ...(IB_DECISION[dataKey] && {
      decisionProofPoints: IB_DECISION[dataKey],
    }),
  };
}
